import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_MESSAGE,
  useGuardCtx,
  useGuardCtxOptional,
  type GuardCtx,
} from './unsavedChangesGuardContext';

/**
 * Register this page as the active unsaved-changes guard while {@code dirty} is true. While
 * registered:
 * <ul>
 *   <li>SPA nav routed through {@link useGuardedNavigate} or {@code GuardedNavLink} prompts
 *       first via the shared confirm modal.</li>
 *   <li>Tab close / refresh / external nav triggers the browser's native confirm.</li>
 *   <li>The browser's Back / Forward buttons prompt via the same shared confirm modal —
 *       slice-3557 (BigList #14); see {@code UnsavedChangesGuard.tsx} for how the history
 *       sentinel that makes this possible works.</li>
 * </ul>
 *
 * <p>Required by {@code docs/dev-rules.md} §4.8 — every Tier-2 maintenance screen must call
 * this with its form's dirty flag.
 */
export function useUnsavedChangesGuard(dirty: boolean, message: string = DEFAULT_MESSAGE): void {
  const { setActive } = useGuardCtx();
  useGuardRegistration(setActive, dirty, message);
}

/**
 * Same guard, but a no-op registration when no {@code <UnsavedChangesGuardProvider>} is
 * mounted (the {@code beforeunload} half still applies).
 *
 * <p>slice-3557 — for SHARED infrastructure that renders in both places ({@code
 * TableBrowserPage}, whose 180+ component tests mount it without the app shell) and for the
 * ~70 existing pages swept in that slice, whose own component tests (114 files) likewise
 * mount them bare. NEW Tier-2 screens use the strict {@link useUnsavedChangesGuard}: a
 * screen that forgets the provider should fail loudly, not lose edits silently.
 */
export function useOptionalUnsavedChangesGuard(
  dirty: boolean,
  message: string = DEFAULT_MESSAGE,
): void {
  const ctx = useGuardCtxOptional();
  useGuardRegistration(ctx?.setActive, dirty, message);
}

/** Shared body of the two hooks above. */
function useGuardRegistration(
  setActive: GuardCtx['setActive'] | undefined,
  dirty: boolean,
  message: string,
): void {
  // This hook instance's identity in the provider's registry — one entry per mounted caller,
  // so a nested component's dirty state can't be clobbered by its parent's clean one.
  const keyRef = useRef<object>({});

  useEffect(() => {
    if (!setActive) return;
    const key = keyRef.current;
    setActive(key, { dirty, message });
    return () => setActive(key, null);
  }, [dirty, message, setActive]);

  useEffect(() => {
    if (!dirty) return;
    // Most modern browsers ignore the custom string and show their own copy, but we set it
    // anyway for the few that still honor it. preventDefault + returnValue='' is the
    // canonical recipe.
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}

/**
 * Drop-in for {@code useNavigate()}. Returns a function that prompts via the active guard
 * before navigating. If the active page is clean (or no guard is registered), it navigates
 * synchronously.
 */
export function useGuardedNavigate() {
  const navigate = useNavigate();
  const { ask } = useGuardCtx();
  return useCallback(
    async (to: string | number, options?: { replace?: boolean }) => {
      const ok = await ask();
      if (ok) {
        // A number is a history delta (-1 = browser Back) — slice-3856 (#3691).
        if (typeof to === 'number') navigate(to);
        else navigate(to, options);
      }
      return ok;
    },
    [navigate, ask],
  );
}
